import { useEffect } from 'react'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { getSubscriptionDiscount, getCustomerSubscriptions, updateSubscription } from 'services/subscriptions'
import { Toast } from 'lib/ui/components'
import { MagentoMoney } from 'lib/types'

export interface SubscriptionData {
  itemFrequency: number
  price: {
    maximum_price: {
      final_price: MagentoMoney
    }
  }
}

interface UpdateSubscriptionType {
  frequency: number
  quantity: number
  status: string
}

interface AttributeCustomOption {
  label?: string
  value?: string
}
export interface OrderSubscriptionItem {
  PDPID?: Number
  frequency_in_cart?: string
  name?: string
  options?: AttributeCustomOption[]
  package_qty?: Number
  price?: string
  qty?: string
  sku?: string
  status?: string
  thumbnail?: string
}

export interface CustomerSubscription {
  created_at?: string
  grand_total?: Number
  halo_order_id?: Number
  subscription_id?: Number
  id?: Number
  order_number: string
  PDPID?: Number
  frequency_in_cart?: string
  name?: string
  options?: AttributeCustomOption[]
  package_qty?: Number
  price?: string
  qty?: string
  sku?: string
  status?: 'H' | 'T' | 'A' | undefined
  thumbnail?: string
}
export interface CustomerSubscriptions {
  items?: CustomerSubscription[]
}

export const useSubscriptionData = (availableFrequencies: any, renderPrice: any) => {
  const subscriptionsData: SubscriptionData[] = availableFrequencies?.map((frequency: string) => {
    const frequencyNumber = parseInt(frequency.replace(/\D+/g, ''))

    return ({
      itemFrequency: frequencyNumber,
      price: renderPrice
    })
  })

  return {
    subscriptionsData
  }
}

export const useFreeShippingData = (productId: number, frequency: number, qty: number) => {
  const { data, loading: loadingSubscription } = useQuery(getSubscriptionDiscount, {
    variables: {
      input: [
        {
          product_id: productId,
          frequency,
          qty
        }
      ]
    }
  })

  const isFreeShipping: Boolean = data?.csGetProductDiscount?.products[0]?.free_shipping
  const itemDiscount: number = data?.csGetProductDiscount?.products[0]?.discount

  return {
    isFreeShipping,
    itemDiscount,
    loadingSubscription
  }
}

export const useSubscriptionOrders = (subscriptionId?: string, sku?: string) => {
  const [getSubscription, { loading: loadingData, data }] = useLazyQuery(getCustomerSubscriptions, {
    fetchPolicy: 'no-cache'
  })
  const [updateCustomerSubscription, { loading: loadingMutation }] = useMutation(updateSubscription, {
    onCompleted: () => {
      getSubscription()
      Toast.toastSuccess({ messageId: 'toast.changesSavedSuccessfully.message' })
    },
    onError: (error) => Toast.toastFailure({ messageId: error.message })
  })
  const subscriptionData: CustomerSubscriptions = data?.customerSubscriptions
  const customerSubscriptions = subscriptionData?.items
  const firstSubscriptionItem = customerSubscriptions?.length && customerSubscriptions[0]

  const subscriptionOrder = customerSubscriptions?.find((item: any) => item?.subscription_id === parseInt(subscriptionId ?? '') &&item?.sku === sku)

  const handleUpdate = ({ frequency, quantity, status }: UpdateSubscriptionType) => {
    const { sku, PDPID, id, halo_order_id: haloOrderId } = subscriptionOrder ?? {}

    updateCustomerSubscription({
      variables: {
        id,
        OrderID: haloOrderId,
        PDPID,
        PDPInterval: frequency,
        PDPIntervalType: 'W',
        Quantity: quantity,
        Status: status,
        SkuID: sku
      }
    })
  }

  const loading = loadingData || loadingMutation

  useEffect(() => {
    getSubscription()
  }, [getSubscription])

  return {
    customerSubscriptions,
    firstSubscriptionItem,
    subscriptionOrder,
    loading,
    handleUpdate
  }
}
